import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { OfferListSortOption } from 'constants/offerListFilters'
import { replace } from 'lib/routing/appRouting'
import { sanitizeUnsupportedListingSearchParams } from 'lib/search/hotelOfferTypeUtils'
import { deleteSearchParams, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { addGTMEvent } from 'api/googleTagManager'
import { searchSwitchViewClick } from 'analytics/eventDefinitions'
import type { OfferListFilterSubmit } from 'components/OfferList/OfferListFilter/OfferListFilters'
import OfferListFilters from 'components/OfferList/OfferListFilter/OfferListFilters'
import { queryKeyAdults, queryKeyChildren, queryKeyDestinationId, queryKeyFilters, queryKeyFlexibleMonths, queryKeyFlexibleNights } from 'constants/url'
import getOfferListKey from 'lib/offer/offerListKey'
import { getHotelEnabledFilters } from 'selectors/filters/filterSelectors'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { saveRecentFilters } from 'components/Search/utils'
import type { RecentFilters } from 'components/Search/type'
import { isHotelSearchResultsPath, withHotelFilterSectionsV2EnabledFilters } from './utils/hotelFilterSectionsV2'

type InheritedOfferListFiltersProps = Pick<ComponentProps<typeof OfferListFilters>, 'onFilterChange' | 'onSortChange'>

interface Props extends InheritedOfferListFiltersProps {
  windowSearch: string
  pathname: string
  availableFilters?: App.OfferListFilterOptions
  filters: App.OfferListFilters
  mapViewPath?: string
  switchView?: {
    label: string
    view: 'map' | 'list'
    URL: string
  }
  sortOptions?: Array<OfferListSortOption>
  overrides?: Partial<App.OfferListAvailableFilters>
  showPropertyTypes?: boolean
  heading?: ReactNode
  shouldIncludeAllSearchFilters?: boolean
  shouldShowFilters?: boolean
  onPopularHolidayTypeFilterClick?: () => void
}

const filterToArray = (value?: string | Array<string>): Array<string> => value ? ([] as Array<string>).concat(value) : []

function HotelSearchPageFilters({
  windowSearch,
  pathname,
  sortOptions = EmptyArray,
  filters = EmptyObject,
  switchView,
  showPropertyTypes,
  heading,
  shouldIncludeAllSearchFilters,
  shouldShowFilters,
  onPopularHolidayTypeFilterClick,
  overrides,
  availableFilters: availableFiltersProp,
  onFilterChange,
  onSortChange,
}: Props) {
  // exclude the location filters from the search results page
  const availableFilters: App.OfferListFilterOptions | undefined = useMemo(() => (
    availableFiltersProp?.filters ? {
      ...availableFiltersProp,
      filters: {
        ...availableFiltersProp.filters,
        ...overrides,
      },
    } : undefined
  ), [availableFiltersProp, overrides])

  const dispatch = useAppDispatch()

  const onFilterApply = useCallback((values: OfferListFilterSubmit) => {
    let search = windowSearch

    const locations = filterToArray(values.destinations)
    const holidayTypes = filterToArray(values.holidayTypes)
    const propertyTypes = filterToArray(values.propertyTypes)
    const amenities = filterToArray(values.amenities)
    const inclusions = filterToArray(values.inclusions)

    const recentFilters: Partial<RecentFilters> = {
      ...(locations.length ? { locations } : {}),
      ...(holidayTypes.length ? { holidayTypes } : {}),
      ...(propertyTypes.length ? { propertyTypes } : {}),
      ...(amenities.length ? { amenities } : {}),
      ...(inclusions.length ? { inclusions } : {}),
      ...(values.customerRatings ? { customerRatingGte: values.customerRatings } : {}),
    }

    if (Object.keys(recentFilters).length) {
      saveRecentFilters(recentFilters)
    }

    search = setSearchParamValue(search, 'locations', locations.join(','))
    search = setSearchParamValue(search, 'holidayTypes', holidayTypes.join(','))
    search = setSearchParamValue(search, 'propertyTypes', propertyTypes.join(','))
    search = setSearchParamValue(search, 'amenities', amenities.join(','))
    search = setSearchParamValue(search, 'inclusions', inclusions.join(','))
    search = setSearchParamValue(search, 'customerRatingGte', values.customerRatings ?? '')

    if (values.sortBy) {
      if (values.sortBy === 'recommended') {
        search = deleteSearchParams(search, 'sortBy')
      } else {
        search = setSearchParamValue(search, 'sortBy', values.sortBy)
      }
    }

    dispatch(replace({ search: sanitizeUnsupportedListingSearchParams(search) }))
  }, [dispatch, windowSearch])

  const resetFilters = () => {
    let filtersToDelete = [...queryKeyFilters]
    if (shouldIncludeAllSearchFilters) {
      filtersToDelete = filtersToDelete.concat([queryKeyDestinationId, queryKeyAdults, queryKeyChildren, queryKeyFlexibleMonths, queryKeyFlexibleNights])
    }
    const search = deleteSearchParams(
      windowSearch,
      ...filtersToDelete,
    )
    dispatch(replace({ search }))
  }

  const onViewSwitch = useCallback(() => {
    addGTMEvent(searchSwitchViewClick())
  }, [])

  const defaultEnabledFilters = useAppSelector(getHotelEnabledFilters)
  const isHotelSearchResultsPage = isHotelSearchResultsPath(pathname)
  const enabledFilters = useMemo(
    () => isHotelSearchResultsPage ? withHotelFilterSectionsV2EnabledFilters(defaultEnabledFilters) : defaultEnabledFilters,
    [defaultEnabledFilters, isHotelSearchResultsPage],
  )

  return (
    <OfferListFilters
      heading={heading}
      availableFilters={availableFilters}
      sortOptions={sortOptions}
      existingFilters={filters}
      mapViewPath={switchView?.view === 'map' ? switchView.URL : undefined}
      listViewPath={switchView?.view === 'list' ? switchView.URL : undefined}
      onFilterApply={onFilterApply}
      onFilterReset={resetFilters}
      onViewSwitch={onViewSwitch}
      isFilterTogglesShown
      showPropertyTypes={showPropertyTypes}
      onPopularHolidayTypeFilterClick={onPopularHolidayTypeFilterClick}
      shouldIncludeAllSearchFilters={shouldIncludeAllSearchFilters}
      shouldShowFilters={shouldShowFilters || false}
      enabledFilters={enabledFilters}
      onFilterChange={onFilterChange}
      onSortChange={onSortChange}
    />
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const offerListKey = getOfferListKey(ownProps.filters ?? {})
  const availableFilters = state.offer.offerListFilterOptions[offerListKey]

  return {
    windowSearch: state.router.location.search,
    pathname: state.router.location.pathname,
    availableFilters,
  }
}

export default connect(mapStateToProps)(HotelSearchPageFilters)
